import React, { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import LandingPage from '../pages/LandingPage';
import PricingPage from '../pages/PricingPage';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import MagicLinkPage from '../pages/MagicLinkPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import ClientJoinPage from '../pages/dashboard/ClientJoinPage';
import ResolveTokenPage from '../pages/ResolveTokenPage';
import NotFoundPage from '../pages/NotFoundPage';
import MentionsLegalesPage from '../pages/legal/MentionsLegalesPage';
import PrivacyPage from '../pages/legal/PrivacyPage';
import CguPage from '../pages/legal/CguPage';
import CgvPage from '../pages/legal/CgvPage';
import RgpdPage from '../pages/legal/RgpdPage';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminRoute from '../components/AdminRoute';
import CONTENT_ROUTE_SLUGS from '../data/content-route-slugs.json';

const ContentRoutePage = React.lazy(() =>
  import(/* webpackChunkName: "content-routes" */ '../pages/content/ContentRoutePage')
);
const ArticleDispatcher = React.lazy(() =>
  import(/* webpackChunkName: "article-routes" */ '../pages/content/ArticleDispatcher')
);
const DashboardPage = React.lazy(() => import('../pages/DashboardPage'));
const AdminDashboardPage = React.lazy(() => import('../pages/AdminDashboardPage'));

function RouteFallback() {
  return (
    <div className="d-flex justify-content-center align-items-center p-5" style={{ minHeight: '40vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading</span>
      </div>
    </div>
  );
}

function LazyDashboard() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <DashboardPage />
    </Suspense>
  );
}

function LazyAdminDashboard() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <AdminDashboardPage />
    </Suspense>
  );
}

function LazyContentRoutePage() {
  return (
    <Suspense fallback={null}>
      <ContentRoutePage />
    </Suspense>
  );
}

function LazyArticleDispatcher() {
  return (
    <Suspense fallback={null}>
      <ArticleDispatcher />
    </Suspense>
  );
}

const CONTENT_ROUTE_DEFS = CONTENT_ROUTE_SLUGS.map((contentPath) => {
  const routePath = contentPath.replace(/^\//, '').replace(/\/$/, '');
  return [routePath, LazyContentRoutePage];
});

function p(en, frPath) {
  if (!en) return frPath;
  return frPath.replace(/^\//, '');
}

/**
 * @param {{ isAuthenticated: boolean, user: object | null, locale: 'fr' | 'en' }} opts
 */
export function buildSharedRouteElements({ isAuthenticated, user, locale }) {
  const en = locale === 'en';
  const prefix = en ? '/en' : '';
  const redirectAuthed = user?.role === 'admin' ? `${prefix}/admin` : `${prefix}/dashboard`;

  const authLogin = !isAuthenticated ? <LoginPage /> : <Navigate to={redirectAuthed} replace />;
  const authRegister = !isAuthenticated ? <RegisterPage /> : <Navigate to={redirectAuthed} replace />;

  const articleRoutes = [
    <Route key="articles-slug" path={p(en, '/articles/:slug')} element={<LazyArticleDispatcher />} />,
    <Route key="articles-slug-slash" path={p(en, '/articles/:slug/')} element={<LazyArticleDispatcher />} />,
  ];

  const contentRoutes = CONTENT_ROUTE_DEFS.flatMap(([routePath, Cmp]) => [
    <Route key={`c-${routePath}`} path={p(en, `/${routePath}`)} element={<Cmp />} />,
    <Route key={`c-${routePath}-slash`} path={p(en, `/${routePath}/`)} element={<Cmp />} />,
  ]);

  return [
    en ? (
      <Route key="home" index element={<LandingPage />} />
    ) : (
      <Route key="home" path="/" element={<LandingPage />} />
    ),
    <Route key="login" path={p(en, '/login')} element={authLogin} />,
    <Route key="register" path={p(en, '/register')} element={authRegister} />,
    <Route key="magic" path={p(en, '/magic-link/:token')} element={<MagicLinkPage />} />,
    <Route key="verify" path={p(en, '/verify-email/:token')} element={<VerifyEmailPage />} />,
    <Route key="join" path={p(en, '/join/:linkId')} element={<ClientJoinPage />} />,
    <Route key="group" path={p(en, '/group/:groupId')} element={<ResolveTokenPage />} />,
    <Route key="resolve" path={p(en, '/resolve-token/:token')} element={<ResolveTokenPage />} />,
    <Route key="l1" path={p(en, '/legal/mentions')} element={<MentionsLegalesPage />} />,
    <Route key="l2" path={p(en, '/legal/privacy')} element={<PrivacyPage />} />,
    <Route key="l3" path={p(en, '/legal/cgu')} element={<CguPage />} />,
    <Route key="l4" path={p(en, '/legal/cgv')} element={<CgvPage />} />,
    <Route key="l5" path={p(en, '/legal/rgpd')} element={<RgpdPage />} />,
    <Route key="tarifs" path={p(en, '/tarifs')} element={<PricingPage />} />,
    <Route key="tarifs-slash" path={p(en, '/tarifs/')} element={<PricingPage />} />,
    ...contentRoutes,
    ...articleRoutes,
    <Route key="404-explicit" path={p(en, '/404')} element={<NotFoundPage />} />,
    <Route
      key="dash"
      path={p(en, '/dashboard/*')}
      element={
        <ProtectedRoute>
          <LazyDashboard />
        </ProtectedRoute>
      }
    />,
    <Route
      key="adm"
      path={p(en, '/admin/*')}
      element={
        <ProtectedRoute>
          <AdminRoute>
            <LazyAdminDashboard />
          </AdminRoute>
        </ProtectedRoute>
      }
    />,
    <Route key="404" path="*" element={<NotFoundPage />} />,
  ];
}
